import React, { useCallback, useMemo, memo } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import { isResearcher } from 'src/utils/checkPermissions';
import { getCompanyInfo } from '../../../../actions/companyDetail';
import { changeBuyerType } from '../../../../actions/company/buyer/info';
import { PureInlineInput as Input } from '../../../../components/helpers/Input';
import Select from '../../../../components/helpers/Select';
import { getIn, unwrap } from '../../../../utils/ChangeSpy';
import uniqueId from '../../../../utils/uniqueId';
import UrlMaker from '../../../../routing/UrlMaker';
import confirm from '../../../../containers/decorators/confirm';
import { CompanyProfile } from './CompanyProfile';
import config from '../../../../config';
import { TYPE_STRATEGIC, TYPE_FINANCIAL } from '../../../../config/constants/common';
import { usePopupContext } from '../../../../contexts/PopupContext';
import { Card } from '../../../helpers/Card';
import Button from '../../../helpers/Button';

import style from './style.module.css';

const statusSelectId = uniqueId();
const highId = uniqueId();
const priId = uniqueId();

const CompanyBuyerStatus = ({
  onChange,
  onHighStatusUpdate,
  harvcoSuggest,
  type,
  buyer,
  statuses,
  isDuplicateCompany,
  userRole,
  companyInfo,
  companyId,
  history,
  changeBuyerType,
  getCompanyInfo,
}) => {
  const popupContext = usePopupContext();

  const getNextBuyerType = useCallback(() => (type === TYPE_FINANCIAL ? TYPE_STRATEGIC : TYPE_FINANCIAL), [type]);

  const checkIsEditableBuyerType = useCallback(() => userRole === config.DIRECTOR, [userRole]);

  const handleConfirmChangeBuyerType = useMemo(() => {
    const recordOwnerId = unwrap(companyInfo.get('recordOwnerId'));
    let confirmMessage = 'Are you sure you wish to proceed?';

    if (recordOwnerId) {
      confirmMessage = `Caution: This Buyer is also a Target Company. By changing the Buyer Type to "Financial", you will disable the Target Company record (Financial Buyers cannot be Targets).
      Please make sure you have saved all relevant data under the Buyer Record before proceeding. \n\n${confirmMessage}`;
    }

    const handler = () => {
      const buyerType = type === TYPE_FINANCIAL ? TYPE_STRATEGIC : TYPE_FINANCIAL;

      const afterSuccess = () => {
        history.replace(`/company/${companyId}/buyer`);
        getCompanyInfo({
          companyId,
          afterSuccess: () => {
            popupContext.closePopup();
          },
          afterError: popupContext.closePopup,
        });
      };

      changeBuyerType({ companyId, buyerType, afterSuccess, afterError: popupContext.closePopup });
    };

    return confirm(confirmMessage, handler, popupContext);
  }, [companyInfo, type, companyId, history, changeBuyerType, popupContext]);

  const handleHighStatusUpdate = useCallback(
    event => {
      event.preventDefault();

      if (!isResearcher(userRole)) {
        onHighStatusUpdate();
      }
    },
    [userRole, onHighStatusUpdate],
  );

  const highStatus = getIn(buyer, 'buyerHighStatus');
  const title = statuses.size
    ? statuses.find(status => getIn(status, ['value']) === highStatus, null, statuses.get(0)).get('name')
    : '';
  const finCls = classNames('form-group form-inline-block', { hidden: type !== TYPE_FINANCIAL });
  const parentLink = UrlMaker.create('/company/:companyId/buyer').mapParams({
    companyId: buyer.get('parentId'),
  });
  let parentBlock = (
    <div className="form-group form-inline-block">
      <b>
        <a href={parentLink} rel="noopener noreferrer" target="_blank">
          Parent: {buyer.getIn(['parent', 'buyerCurrentStatus'], '')}/{buyer.getIn(['parent', 'buyerHighStatus'], '')}
        </a>
      </b>
    </div>
  );

  if (type !== TYPE_STRATEGIC || !buyer.get('parentId')) {
    parentBlock = null;
  }

  const buyerTypeButton = checkIsEditableBuyerType() ? (
    <Button onClick={handleConfirmChangeBuyerType}>Change to {getNextBuyerType()}</Button>
  ) : null;

  const isDisabled = isResearcher(userRole) || isDuplicateCompany;

  return (
    <Card as="form" className={style.host} onChange={onChange}>
      <div className={classNames(style.group, style.typeGroup, 'col-xs-3 form-inline')}>
        <div className="form-group form-inline-block">
          <b>
            Buyer Type: {type} {buyerTypeButton}
          </b>
        </div>
      </div>
      <div className={classNames(style.group, 'col-xs-6 form-inline')}>
        <div className={classNames(style.status, 'form-group form-inline-block')}>
          <label htmlFor={statusSelectId}>Status</label>
          <Select
            disabled={isDisabled}
            emptyValue=""
            id={statusSelectId}
            label="Status"
            name="buyerCurrentStatus"
            onChange={onChange}
            options={statuses}
            root={buyer}
            selectClassName="input-sm"
            wrapperClassName="control-wrap"
            deep
            showValue
          />
        </div>

        <div className="form-group w160 form-inline-block">
          <Input
            className={classNames(style.highStatus, 'input-sm')}
            id={highId}
            label="High"
            name="buyerHighStatus"
            onClick={handleHighStatusUpdate}
            placeholder="High"
            root={buyer}
            title={title}
            deep
            readOnly
            showLabel
          />
        </div>

        <div className={finCls}>
          <b>
            CEO: {buyer.get('ceoAbc', '')}/{buyer.get('ceo123', '')}
          </b>
        </div>

        <CompanyProfile
          className={finCls}
          disabled={isDuplicateCompany}
          id={statusSelectId}
          statuses={statuses}
          subsidiaries={buyer.get('subsidiaries')}
        />

        {parentBlock}

        <div className="form-group w160 form-inline-block">
          <Input
            className="input-sm"
            disabled={isDisabled}
            id={priId}
            label="Priority"
            name="buyerPriority"
            onChange={onChange}
            placeholder="Priority"
            root={buyer}
            deep
            showLabel
          />
        </div>
      </div>
      <div className={classNames(style.group, style.contactGroup, 'col-xs-3 text-right form-inline')}>
        {harvcoSuggest}
      </div>
    </Card>
  );
};

CompanyBuyerStatus.propTypes = {
  isDuplicateCompany: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  userRole: state.auth.getIn(['user', 'roles', 0, 'slug']),
  companyInfo: state.targetCompany.info.get('info'),
});

export { CompanyBuyerStatus };
export default connect(mapStateToProps, {
  changeBuyerType,
  getCompanyInfo,
})(withRouter(memo(CompanyBuyerStatus)));
